/**
 * Configuration for success step variants
 * Defines all possible page states based on availability and business hours
 */

import { SuccessStepConfig, SuccessStepVariant, SuccessStepContext, ComponentType } from './types';
import { simulationService } from '../../../services/simulation';

// Multi-variant test split - randomly assign variant group
// Use sessionStorage to keep consistent for the entire session
type VariantGroup = 'A' | 'B' | 'C' | 'D';

const getVariantGroup = (): VariantGroup => {
  // Check for simulation override first
  const simulationOverride = simulationService.shouldOverrideAbTestGroup();
  if (simulationOverride.override && simulationOverride.group) {
    return simulationOverride.group as VariantGroup;
  }

  const storageKey = 'success_step_ab_test';
  const stored = sessionStorage.getItem(storageKey);
  
  if (stored === 'A' || stored === 'B' || stored === 'C' || stored === 'D') {
    return stored as VariantGroup;
  }
  
  // 25% split across 4 variants:
  // A: 30s auto-dialer + schedule button (original)
  // B: 60s auto-dialer + "Missed our call?" message
  // C: 30s auto-dialer + "Missed our call?" message
  // D: Click-to-call button (no auto-dialer)
  const random = Math.random();
  let group: VariantGroup;
  if (random < 0.35) {
    group = 'A';
  } else if (random < 0.7) {
    group = 'B';
  } else if (random < 0.85) {
    group = 'C';
  } else {
    group = 'D';
  }
  
  sessionStorage.setItem(storageKey, group);
  return group;
};

export const successStepConfig: SuccessStepConfig = {
  variants: [
    // Highest priority: Scheduled appointment
    // NOTE: UI-only variant - NOT tracked to backend (user already completed scheduling action)
    {
      id: 'appointment-scheduled',
      priority: 1,
      shouldShow: (context) => context.scheduledTime !== null,
      components: ['message', 'callButton'],
      messageConfig: {
        title: 'Appointment Scheduled',
        description: 'For immediate assistance before your appointment, call:',
      },
    },

    // High priority: Call already initiated
    // NOTE: UI-only variant - NOT tracked to backend (call already initiated via API)
    {
      id: 'call-initiated',
      priority: 2,
      shouldShow: (context) => context.isCallInitiated,
      components: ['message'],
      messageConfig: {
        title: 'Call in Progress',
        description: 'Please answer the incoming call.',
      },
    },

    // BPO agents - no auto-dialer (high availability)
    {
      id: 'high-bpo-agent-manual-call',
      priority: 3,
      shouldShow: (context) => 
        context.contactCenterAvailability === 'high' &&
        context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isCallInitiated &&
        !context.isHoliday,
      components: ['message', 'callButton', 'scheduleButton'],
      messageConfig: {
        title: 'Contact Our Solution Specialists',
        description: 'Please call us directly or schedule a callback for a convenient time.',
      },
    },

    // BPO agents - no auto-dialer (low availability)
    {
      id: 'low-bpo-agent-manual-call',
      priority: 4,
      shouldShow: (context) => 
        context.contactCenterAvailability === 'low' &&
        context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isCallInitiated &&
        !context.isHoliday,
      components: ['message', 'callButton', 'scheduleButton'],
      messageConfig: {
        title: 'Contact Our Solution Specialists',
        description: 'Please call us directly or schedule a callback for a convenient time.',
      },
    },

    // None availability - immediate schedule form (all conditions)
    // Note: Component logic shows schedule form immediately when availability is 'none'
    {
      id: 'none-availability-immediate-schedule',
      priority: 5,
      shouldShow: (context) => 
        (context.contactCenterAvailability === 'none' || context.contactCenterAvailability === null) &&
        !context.isCallInitiated,
      components: [], // Empty - schedule form is shown directly by component logic
      messageConfig: {
        title: 'Schedule Your Consultation',
        description: 'Please schedule a convenient time for us to call you.',
      },
    },

    // Outside TCPA hours or holidays (high availability) - GROUP A (Old: all buttons immediately)
    {
      id: 'high-outside-call-hours',
      priority: 6,
      shouldShow: (context) => 
        getVariantGroup() === 'A' &&
        context.contactCenterAvailability === 'high' &&
        (!context.isWithinCallHours || context.isHoliday) && 
        !context.isCallInitiated,
      components: ['message', 'callButton', 'scheduleButton'],
      messageConfig: {
        title: 'Schedule a Callback',
        description: 'We have some agents awaiting your call. Please call us directly or schedule a callback.',
      },
    },

    // Outside TCPA hours or holidays (high availability) - GROUP B (New: delayed schedule button)
    {
      id: 'high-outside-call-hours-2',
      priority: 6,
      shouldShow: (context) => 
        getVariantGroup() === 'B' &&
        context.contactCenterAvailability === 'high' &&
        (!context.isWithinCallHours || context.isHoliday) && 
        !context.isCallInitiated,
      components: (context): ComponentType[] => {
        const components: ComponentType[] = ['message', 'callButton'];
        if (context.showScheduleButton) {
          components.push('scheduleButton');
        }
        return components;
      },
      messageConfig: {
        title: 'Call Now to Finalize Your Offer.',
        description: '',
      },
      delayScheduleButton: 30000, // Show schedule button after 30 seconds
    },

    // Outside TCPA hours or holidays (low availability) - GROUP A (Old: all buttons immediately)
    {
      id: 'low-outside-call-hours',
      priority: 7,
      shouldShow: (context) => 
        getVariantGroup() === 'A' &&
        context.contactCenterAvailability === 'low' &&
        (!context.isWithinCallHours || context.isHoliday) && 
        !context.isCallInitiated,
      components: ['message', 'callButton', 'scheduleButton'],
      messageConfig: {
        title: 'Schedule a Callback',
        description: 'We have some agents awaiting your call. Please call us directly or schedule a callback.',
      },
    },

    // Outside TCPA hours or holidays (low availability) - GROUP B (New: delayed schedule button)
    {
      id: 'low-outside-call-hours-2',
      priority: 7,
      shouldShow: (context) => 
        getVariantGroup() === 'B' &&
        context.contactCenterAvailability === 'low' &&
        (!context.isWithinCallHours || context.isHoliday) && 
        !context.isCallInitiated,
      components: (context): ComponentType[] => {
        const components: ComponentType[] = ['message', 'callButton'];
        if (context.showScheduleButton) {
          components.push('scheduleButton');
        }
        return components;
      },
      messageConfig: {
        title: 'Call Now to Finalize Your Offer.',
        description: '',
      },
      delayScheduleButton: 30000, // Show schedule button after 30 seconds
    },

    // High availability - auto dialer - GROUP A (30s countdown + schedule button)
    {
      id: 'high-availability-auto-call',
      priority: 8,
      shouldShow: (context) => 
        getVariantGroup() === 'A' &&
        context.contactCenterAvailability === 'high' &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: (context): ComponentType[] => {
        const components: ComponentType[] = ['phoneDialer'];
        if (context.showScheduleButton) {
          components.push('scheduleButton');
        }
        return components;
      },
      dialerDuration: 30,
    },

    // High availability - auto dialer - GROUP B (60s countdown + "Missed our call?" message)
    {
      id: 'high-availability-auto-call-2',
      priority: 8,
      shouldShow: (context) => 
        getVariantGroup() === 'B' &&
        context.contactCenterAvailability === 'high' &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: (context): ComponentType[] => {
        if (context.showMissedCallMessage) {
          return ['missedCallMessage'];
        }
        return ['phoneDialer'];
      },
      dialerDuration: 60,
      delayMissedCallMessage: 60000,
    },

    // High availability - auto dialer - GROUP C (30s countdown + "Missed our call?" message)
    {
      id: 'high-availability-auto-call-3',
      priority: 8,
      shouldShow: (context) => 
        getVariantGroup() === 'C' &&
        context.contactCenterAvailability === 'high' &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: (context): ComponentType[] => {
        if (context.showMissedCallMessage) {
          return ['missedCallMessage'];
        }
        return ['phoneDialer'];
      },
      dialerDuration: 30,
      delayMissedCallMessage: 30000,
    },

    // High availability - click to call - GROUP D (No auto-dialer, immediate call button)
    {
      id: 'high-availability-click-to-call',
      priority: 8,
      shouldShow: (context) => 
        getVariantGroup() === 'D' &&
        context.contactCenterAvailability === 'high' &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: ['message', 'callButton'],
      messageConfig: {
        title: 'Call Now to Finalize Your Offer.',
        description: '',
      },
    },


    // Low availability weekday - auto dialer - GROUP A (30s countdown + schedule button)
    {
      id: 'low-availability-weekday-auto-call',
      priority: 9,
      shouldShow: (context) => 
        getVariantGroup() === 'A' &&
        context.contactCenterAvailability === 'low' &&
        !context.isWeekend &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: (context): ComponentType[] => {
        const components: ComponentType[] = ['phoneDialer'];
        if (context.showScheduleButton) {
          components.push('scheduleButton');
        }
        return components;
      },
      dialerDuration: 30,
    },

    // Low availability weekday - auto dialer - GROUP B (60s countdown + "Missed our call?" message)
    {
      id: 'low-availability-weekday-auto-call-2',
      priority: 9,
      shouldShow: (context) => 
        getVariantGroup() === 'B' &&
        context.contactCenterAvailability === 'low' &&
        !context.isWeekend &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: (context): ComponentType[] => {
        if (context.showMissedCallMessage) {
          return ['missedCallMessage'];
        }
        return ['phoneDialer'];
      },
      dialerDuration: 60,
      delayMissedCallMessage: 60000,
    },

    // Low availability weekday - auto dialer - GROUP C (30s countdown + "Missed our call?" message)
    {
      id: 'low-availability-weekday-auto-call-3',
      priority: 9,
      shouldShow: (context) => 
        getVariantGroup() === 'C' &&
        context.contactCenterAvailability === 'low' &&
        !context.isWeekend &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: (context): ComponentType[] => {
        if (context.showMissedCallMessage) {
          return ['missedCallMessage'];
        }
        return ['phoneDialer'];
      },
      dialerDuration: 30,
      delayMissedCallMessage: 30000,
    },

    // Low availability weekday - click to call - GROUP D (No auto-dialer, immediate call button)
    {
      id: 'low-availability-weekday-click-to-call',
      priority: 9,
      shouldShow: (context) => 
        getVariantGroup() === 'D' &&
        context.contactCenterAvailability === 'low' &&
        !context.isWeekend &&
        !context.isCallInitiated &&
        !context.isBpoAgent &&
        context.isWithinCallHours &&
        !context.isHoliday,
      components: ['message', 'callButton'],
      messageConfig: {
        title: 'Call Now to Finalize Your Offer.',
        description: '',
      },
    },

    // Low availability weekend - manual call - GROUP A (all buttons immediately)
    {
      id: 'low-availability-weekend-manual',
      priority: 10,
      shouldShow: (context) => 
        getVariantGroup() === 'A' &&
        context.contactCenterAvailability === 'low' &&
        context.isWeekend &&
        !context.isCallInitiated &&
        context.isWithinCallHours,
      components: ['message', 'callButton', 'scheduleButton'],
      messageConfig: {
        title: 'Contact Our Solution Specialists',
        description: 'We have some agents awaiting your call. Please call us directly or schedule a callback for a more convenient time.',
      },
    },

    // Low availability weekend - manual call - GROUP B (New: delayed schedule button)
    {
      id: 'low-availability-weekend-manual-2',
      priority: 10,
      shouldShow: (context) => 
        getVariantGroup() === 'B' &&
        context.contactCenterAvailability === 'low' &&
        context.isWeekend &&
        !context.isCallInitiated &&
        context.isWithinCallHours,
      components: (context): ComponentType[] => {
        const components: ComponentType[] = ['message', 'callButton'];
        if (context.showScheduleButton) {
          components.push('scheduleButton');
        }
        return components;
      },
      messageConfig: {
        title: 'Call Now to Finalize Your Offer.',
        description: '',
      },
      delayScheduleButton: 30000, // Show schedule button after 30 seconds
    },

    // Fallback: Schedule only
    {
      id: 'fallback-schedule-only',
      priority: 999,
      shouldShow: () => true,
      components: ['message', 'scheduleButton'],
      messageConfig: {
        title: 'Schedule Your Consultation',
        description: 'Please schedule a convenient time to discuss your application.',
      },
    },
  ],
};

export type ResolvedSuccessStepVariant = Omit<SuccessStepVariant, 'components'> & {
  components: ComponentType[];
};

/**
 * Selects the appropriate variant based on current context
 * Returns the highest priority variant where shouldShow returns true
 */
export const selectVariant = (context: SuccessStepContext): ResolvedSuccessStepVariant => {
  const sorted = [...successStepConfig.variants].sort((a, b) => a.priority - b.priority);
  
  const selected = sorted.find((variant) => variant.shouldShow(context));
  
  if (!selected) {
    throw new Error('No variant matched - this should never happen with fallback');
  }

  // Resolve components if it's a function
  const components = typeof selected.components === 'function'
    ? selected.components(context)
    : selected.components;

  return {
    ...selected,
    components,
  };
};
